import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { api } from "./client";
import { queryKeys } from "./keys";
import type {
  ListResponse,
  RecordRefillStepInput,
  RefillStep,
  ReviseRefillStepInput,
} from "./types";

const sessionsBase = "/v1/refill-sessions";
const stepsBase = "/v1/refill-steps";

export function useRefillSteps(sessionID: string | undefined) {
  return useQuery({
    queryKey: sessionID
      ? queryKeys.refillSteps.forSession(sessionID)
      : queryKeys.refillSteps.forSession(""),
    queryFn: ({ signal }) =>
      api.get<ListResponse<RefillStep>>(
        `${sessionsBase}/${sessionID}/steps`,
        undefined,
        signal,
      ),
    enabled: !!sessionID,
  });
}

export function useRefillStep(id: string | undefined) {
  return useQuery({
    queryKey: id
      ? queryKeys.refillSteps.detail(id)
      : queryKeys.refillSteps.detail(""),
    queryFn: ({ signal }) =>
      api.get<RefillStep>(`${stepsBase}/${id}`, undefined, signal),
    enabled: !!id,
  });
}

export function useRecordRefillStep(sessionID: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: RecordRefillStepInput) =>
      api.post<RefillStep>(`${sessionsBase}/${sessionID}/steps`, input),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: queryKeys.refillSteps.forSession(sessionID),
      });
      qc.invalidateQueries({ queryKey: queryKeys.refillSessions.all });
    },
  });
}

export function useReviseRefillStep(stepID: string, sessionID: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ReviseRefillStepInput) =>
      api.post<RefillStep>(`${stepsBase}/${stepID}:revise`, input),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: queryKeys.refillSteps.forSession(sessionID),
      });
    },
  });
}

export function useVoidRefillStep(stepID: string, sessionID: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (reason: string) =>
      api.delete<void>(`${stepsBase}/${stepID}`, { reason }),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: queryKeys.refillSteps.forSession(sessionID),
      });
    },
  });
}

export function useUndoLastRefillStep(sessionID: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (reason: string) =>
      api.post<RefillStep>(`${sessionsBase}/${sessionID}/steps:undo-last`, {
        reason,
      }),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: queryKeys.refillSteps.forSession(sessionID),
      });
    },
  });
}
